// src/App.js
//
// This one static build's real entry point: decides, at runtime, in the
// browser, which of now FOUR completely separate app trees to mount —
// TenantApp (the POS app itself, every business's own subdomain),
// PlatformApp (admin.posflect.com, Super Admin only), MarketingApp
// (posflect.com/www.posflect.com, anonymous marketing visitors), or —
// new as of this feature's own plan doc (posflect project, "ecommerce-
// storefront-plan.md") Phase 1 — StorefrontApp (an anonymous SHOPPER
// visiting one specific Store's own public storefront) — see
// isPlatformHost/isMarketingHost/useSiteContext below for exactly how
// each host is recognized.
//
// All four are React.lazy() dynamic imports rather than plain top-level
// imports — this feature's own bundle-size audit's own top finding: with
// plain imports, a marketing visitor's browser downloaded the ENTIRE
// tenant POS app (TenantApp used to be ~1200 lines of routes + ~100 page
// component imports, right here in this file) plus the entire Platform
// admin panel, just to render a landing page, because a bundler inlines
// anything reachable via a plain `import`, whether or not it's ever
// actually rendered. TenantApp's own former body now lives in its own
// file, TenantApp.js (see that file's own doc comment) — the same
// "completely separate tree, its own file" shape PlatformApp.js and
// MarketingApp.js already had before this change, which is exactly what
// makes them trivial to lazy-load too: no file split needed for those
// two, just wrapping their existing default export in lazy(() =>
// import(...)). StorefrontApp.js is built the same way from day one —
// its own file under pages/Storefront/, its own BrowserRouter, no shared
// state with any of the other three trees, mirroring MarketingApp.js's
// own "these trees must never collide" doc comment.
//
// NoIndexNonMarketingHost is deliberately kept OUTSIDE every <Suspense>
// boundary below, as a plain immediate sibling — it must set its
// <meta name="robots"> tag the instant App() itself renders, not delayed
// behind whichever lazy chunk is still downloading, since a crawler that
// gives up before that chunk resolves must still see the override. As of
// StorefrontApp, it is ONLY rendered for the confirmed TenantApp branch
// (see this file's own bottom half) — a real public storefront WANTS to
// be indexed (this feature's own plan doc §3.10, SEO, not built yet, but
// there is no reason to actively fight it in the meantime), so it must
// never be forced to noindex the way a tenant admin/POS host already is.
import { lazy, Suspense, useEffect, useState } from "react";
import { getSiteContext } from "./services/api";

// See this file's own doc comment above for why these four are lazy
// rather than plain imports. TenantApp is the common case for anyone who
// isn't visiting admin.posflect.com, posflect.com, or a Store's own
// storefront directly, so its own Suspense fallback (AppLoadingFallback,
// below) is intentionally minimal — a returning tenant user's browser
// already has this chunk cached from their last visit in the
// overwhelming majority of cases anyway.
const TenantApp = lazy(() => import("./TenantApp"));
const PlatformApp = lazy(() => import("./PlatformApp"));
const MarketingApp = lazy(() => import("./pages/Marketing/MarketingApp"));
const StorefrontApp = lazy(() => import("./pages/Storefront/StorefrontApp"));

// isPlatformHost decides, at runtime, in the browser, which of this one
// static build's app trees to mount — see PlatformApp.js's own doc
// comment on why that split exists at the component level rather than as
// one more branch inside TenantApp.js's own giant <Routes> tree (see this
// file's own top-of-file doc comment for why that tree now lives in its
// own file rather than here).
// process.env.REACT_APP_PLATFORM_HOST is a
// LOCAL-DEV override only (a dev machine has no real "admin.posflect.com"
// DNS entry to visit) — every real deployment instead matches this exact
// build's own runtime hostname's leading label against "admin", the same
// convention this feature's own plan doc and platform_routes.go's own doc
// comment already name (deploy/posflect-provision-subdomain.sh's ROOT_DOMAIN
// is what the wildcard DNS record and every company's own subdomain are
// built from; "admin" is simply never a company's own subdomain — see
// platform.ValidateSubdomainFormat's reserved-word blocklist).
const PLATFORM_HOST = process.env.REACT_APP_PLATFORM_HOST;
const isPlatformHost = PLATFORM_HOST
  ? window.location.hostname === PLATFORM_HOST
  : window.location.hostname.split(".")[0] === "admin";

// isMarketingHost is this feature's own third branch — the root domain
// itself, posflect.com (and its "www" alias), gets MarketingApp instead of
// silently falling into TenantApp and 400ing on every API call with
// TENANT_NOT_FOUND (see this feature's own plan doc's Grounding section).
// Deliberately an EXPLICIT hostname check, not a generic "fewer than 3
// dot-labels" mirror of the backend's own subdomain-extraction logic —
// that generic rule would also match bare "localhost", which would break
// every developer's existing local TenantApp dev workflow (none of them
// visit a real posflect.com in local dev). REACT_APP_MARKETING_HOST is a
// LOCAL-DEV override only, same convention as REACT_APP_PLATFORM_HOST just
// above. Checked only when isPlatformHost is already false — "admin" must
// always win if somehow both matched.
const MARKETING_HOST = process.env.REACT_APP_MARKETING_HOST;
const isMarketingHost = !isPlatformHost && (
  MARKETING_HOST
    ? window.location.hostname === MARKETING_HOST
    : window.location.hostname === "posflect.com" || window.location.hostname === "www.posflect.com"
);

// useSiteContext answers the ONE question isPlatformHost/isMarketingHost
// above can't: for every OTHER host (an ordinary "<label>.posflect.com"
// address), is this a company's own tenant admin/POS panel, or one of
// that company's Stores' own public storefronts? Unlike the two checks
// above, this can't be decided from the hostname's own SHAPE in the
// browser — both cases look identical (one dot-label under posflect.com)
// — only the backend actually knows, because middlewares.TenantResolver
// already queried both the companies and stores tables from the same
// Host header (see that middleware's own doc comment). So this is a
// network call, not a sync string comparison, and it's SKIPPED entirely
// on the platform/marketing branches, which never need it.
//
// Fails open to isStore:false (i.e. behaves exactly as this app always
// has, defaulting to TenantApp) on any network error — same reasoning
// TenantApp's own login screen already has to apply to every other
// backend hiccup: a shopper hitting a genuine Store host during a brief
// backend blip is a far rarer event than a tenant admin's own browser
// having some transient fetch failure, and stranding EVERY visitor on a
// blank screen because of one failed request would be strictly worse
// than the rare case of briefly mis-routing a storefront visitor to a
// tenant login screen (which 404s cleanly via its own API calls anyway).
function useSiteContext() {
  const skip = isPlatformHost || isMarketingHost;
  const [state, setState] = useState(
    skip ? { loading: false, isStore: false, store: null } : { loading: true, isStore: false, store: null }
  );

  useEffect(() => {
    if (skip) return;
    let cancelled = false;
    getSiteContext()
      .then((ctx) => {
        if (cancelled) return;
        setState({ loading: false, isStore: !!ctx?.is_store, store: ctx?.store || null });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ loading: false, isStore: false, store: null });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}

// NoIndexNonMarketingHost is this feature's own SEO plan doc's other half —
// public/index.html's own static <meta name="robots" content="index,
// follow"> is written from posflect.com's own point of view (see that
// file's own comment on why), since that's the one host actually meant to
// be found. The Platform admin panel and every tenant's own POS/admin
// subdomain must NOT be indexed: there's nothing on either worth surfacing
// in search results, and it would only ever show a login screen or leak
// that a given business subdomain exists at all. A single static
// robots.txt can't tell hosts apart (same file is served on every one of
// them), so this is the actual per-host enforcement — flipped to
// "noindex, nofollow" here, at runtime, the moment this app knows which
// host it's actually running on.
//
// Deliberately NEVER rendered for a Store's own storefront host (see this
// file's own bottom half) — a public storefront is the opposite case from
// a tenant admin panel: a merchant WANTS their product pages found by
// search engines (this feature's own plan doc §3.10 covers real SEO,
// Meta/Structured Data/Sitemap, not built yet — but there is no reason to
// actively suppress indexing in the meantime just because this component
// happens to run on the same kind of hostname a tenant admin panel does).
//
// A crawler that doesn't execute JavaScript never sees this override and
// falls back to index.html's own "index, follow" default regardless of
// host; in practice that's a low real-world risk for the tenant admin
// case (nothing links to a random tenant subdomain from anywhere else on
// the web), and Google/Bing — the two crawlers that matter most — both
// execute this exactly like any other visitor's browser would.
function NoIndexNonMarketingHost() {
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "robots";
      document.head.appendChild(meta);
    }
    meta.content = "noindex, nofollow";
  }, []);

  return null;
}

// AppLoadingFallback — the Suspense fallback for whichever of the four
// lazy trees above is still downloading its own chunk, ALSO reused as the
// plain (non-Suspense) loading state while useSiteContext's own network
// call is still in flight. Deliberately styled inline (no external CSS
// import, no dependency on any of the four trees' own stylesheets — all
// of which are themselves part of the chunk this fallback exists to cover
// for) so it can render instantly with zero extra network requests, the
// same reasoning MarketingApp.js's own MarketingSuspenseFallback doc
// comment gives for reusing already-shipped CSS rather than adding a
// second one — this one goes a step further and ships no CSS of its own
// at all, since even that file's own "already shipped" CSS is itself
// behind a lazy boundary from this file's point of view.
function AppLoadingFallback() {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f6f7fb",
        color: "#4b5568",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        fontSize: 14,
        letterSpacing: "0.04em",
      }}
    >
      POSFLECT
    </div>
  );
}

export default function App() {
  const siteContext = useSiteContext();

  if (isPlatformHost) {
    return (
      <>
        <NoIndexNonMarketingHost />
        <Suspense fallback={<AppLoadingFallback />}>
          <PlatformApp />
        </Suspense>
      </>
    );
  }
  if (isMarketingHost) {
    return (
      <Suspense fallback={<AppLoadingFallback />}>
        <MarketingApp />
      </Suspense>
    );
  }

  // Neither platform nor marketing — still waiting on useSiteContext's
  // own /api/public/site-context call to learn whether this host is a
  // Store's own storefront or an ordinary tenant admin/POS panel. No
  // NoIndexNonMarketingHost yet either way: see that component's own doc
  // comment on why a Store host must never get it, and there is no harm
  // in a crawler briefly seeing index.html's own static default for the
  // handful of milliseconds this call is in flight.
  if (siteContext.loading) {
    return <AppLoadingFallback />;
  }

  if (siteContext.isStore) {
    return (
      <Suspense fallback={<AppLoadingFallback />}>
        <StorefrontApp initialStore={siteContext.store} />
      </Suspense>
    );
  }

  return (
    <>
      <NoIndexNonMarketingHost />
      <Suspense fallback={<AppLoadingFallback />}>
        <TenantApp />
      </Suspense>
    </>
  );
}
